import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Presentation, AlertTriangle, Plus, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

export default function TeachersDirectory() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManageStatus = hasRole("super_admin", "direction", "administration");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("all");
  const [group, setGroup] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/people/teachers").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  const create = async (v) => { try { await api.post("/people/teachers", v); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const archive = async (id, hasAssign) => {
    const msg = hasAssign ? "Ce professeur a des affectations actives. Archiver quand même ?" : `${t("archive")} ?`;
    if (!window.confirm(msg)) return;
    try { const { data } = await api.patch(`/people/teachers/${id}/archive`); data.warning ? toast.warning(data.warning) : toast.success(t("teacherArchived")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const reactivate = async (id) => {
    try { await api.patch(`/people/teachers/${id}/reactivate`); toast.success(t("teacherReactivated")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const statusBadge = (s) => {
    const st = s || "active";
    const cls = st === "archived" ? "bg-slate-200 text-slate-600" : st === "suspended" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
    const label = t(`ust_${st}`) !== `ust_${st}` ? t(`ust_${st}`) : st;
    return <Badge className={`border-0 ${cls}`}>{label}</Badge>;
  };
  useEffect(() => {
    load();
  }, []);
  const subjects = [...new Set(rows.flatMap((r) => r.subject_names || []))];
  const groups = [...new Set(rows.flatMap((r) => r.group_names || []))];
  const filtered = rows.filter((tt) =>
    (!q || (tt.name || "").toLowerCase().includes(q.toLowerCase()) || (tt.email || "").toLowerCase().includes(q.toLowerCase())) &&
    (subject === "all" || (tt.subject_names || []).includes(subject)) &&
    (group === "all" || (tt.group_names || []).includes(group)) &&
    (status === "all" || (status === "unassigned" && !tt.has_assignments) ||
      (status === "archived" && tt.status === "archived") || (status === "active" && tt.status !== "archived")));
  return (
    <div data-testid="teachers-directory">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("teacherFiles")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button className="gap-2" data-testid="add-teacher-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("addTeacher")}</Button>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className="w-40" data-testid="teacher-filter-subject"><SelectValue placeholder={t("allSubjects")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allSubjects")}</SelectItem>{subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-40" data-testid="teacher-filter-group"><SelectValue placeholder={t("allGroups")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allGroups")}</SelectItem>{groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="teacher-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="active">{t("ust_active")}</SelectItem>
              <SelectItem value="archived">{t("archived")}</SelectItem>
              <SelectItem value="unassigned">{t("unassignedFilter")}</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="teacher-search" className="pl-9 w-52" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>
      <EntityDialog open={open} onOpenChange={setOpen} title={t("addTeacher")} submitLabel={t("create")} onSubmit={create}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") }]} />
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("teacherCode")}</TableHead><TableHead>{t("name")}</TableHead><TableHead>{t("subject")}</TableHead>
            <TableHead>{t("group")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("viewTeacher")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((tt) => (
              <TableRow key={tt.id} data-testid="teacher-dir-row">
                <TableCell className="font-mono text-xs text-slate-600">{tt.teacher_code}</TableCell>
                <TableCell className="font-medium text-slate-800">{tt.name}
                  {!tt.has_assignments && <Badge className="ml-2 bg-amber-100 text-amber-700 border-0 gap-1"><AlertTriangle className="h-3 w-3" />{t("unassignedFilter")}</Badge>}
                </TableCell>
                <TableCell className="text-slate-500 text-xs">{(tt.subject_names || []).join(", ") || "—"}</TableCell>
                <TableCell className="text-slate-500 text-xs">{(tt.group_names || []).join(", ") || "—"}</TableCell>
                <TableCell>{statusBadge(tt.status)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canManageStatus && (tt.status === "archived"
                      ? <Button size="sm" variant="ghost" className="gap-1 text-emerald-600" data-testid={`reactivate-teacher-${tt.id}`} onClick={() => reactivate(tt.id)}><RotateCcw className="h-4 w-4" />{t("reactivate")}</Button>
                      : <Button size="sm" variant="ghost" className="gap-1 text-red-600" data-testid={`archive-teacher-${tt.id}`} onClick={() => archive(tt.id, tt.has_assignments)}><Archive className="h-4 w-4" />{t("archive")}</Button>)}
                    <Link to={`/people/teachers/${tt.id}`} data-testid={`open-teacher-${tt.id}`}>
                      <Button size="sm" variant="outline" className="gap-2"><Presentation className="h-4 w-4" />{t("viewTeacher")}</Button>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
